'use client'

import React, { forwardRef } from 'react'

import {
  cardStyles,
  childrenStyles,
  contentStyles,
  cardLinkChevronStyles,
  footerStyles,
} from './styles'
import { CardProps } from './Card.types'
import { Link, LinkType } from '@sidecar-components/atoms/Link'
import { Icon, IconSize, IconType } from '@sidecar-components/atoms/Icon'
import { Sentiment } from '@sidecar-components/types'
import { BackgroundShapes } from './BackgroundShapes'

/**
 * The Card component is a versatile UI element used to display content in a
 * structured format. It supports various states such as being clickable,
 * linked, or static, and can include optional footers and chevrons.
 *
 * @param {React.ReactNode} children - Optional. The content of the card,
 *   displayed in the main body.
 * @param {string} [className] - Optional. Additional CSS classes for the card
 *   container.
 * @param {string} [childrenClass] - Optional. Additional CSS classes for the
 *   children wrapper.
 * @param {string} [contentClass] - Optional. Additional CSS classes for the
 *   content wrapper. Mainly used to mess with the card padding.
 * @param {boolean} [hasBgShapes] - Optional. Indicates if the card has
 *   background shades, affecting its visual style.
 * @param {boolean} [hasList] - Optional. Indicates if the card contains a list.
 * @param {string} [id] - Optional. Unique identifier for the card.
 * @param {boolean} [hideChevron=false] - Optional. Hides the chevron icon on a
 *   clickable card when true. Default is `false`
 * @param {{ path: string; type: LinkType }} [link] - Optional. Configuration
 *   for the card link. If `path` is non-empty, the card becomes a clickable
 *   link.
 * @param {React.ReactNode} linkComponent - Component to use for the next.js
 *   Link.
 * @param {function} [onClick] - Optional. Click handler for the card. Makes the
 *   card clickable if provided.
 * @param {Sentiment} [sentiment=Sentiment.DEFAULT] - Optional. Visual sentiment
 *   of the card, which affects its appearance. Default is `Sentiment.DEFAULT`
 * @param {string} [testId] - Optional. Data attribute for QA testing.
 * @param {React.ReactNode} [footer] - Optional. Footer content displayed at the
 *   bottom of the card.
 * @param {object} [rest] - Optional. Additional props passed to the card
 *   element.
 * @param {React.Ref} forwardedRef - Ref forwarded to the card DOM element or
 *   link.
 * @returns {React.ReactNode} A styled card component with support for links,
 *   click handlers, and various customizations.
 */
export const Card = forwardRef(
  (
    {
      children,
      className = '',
      contentClass = '',
      childrenClass = '',
      hasList,
      id = '',
      link = { path: '', type: LinkType.EXTERNAL },
      linkComponent,
      hasBgShapes,
      hideChevron = false,
      onClick,
      sentiment = Sentiment.DEFAULT,
      testId,
      footer,
      ...rest
    }: CardProps,
    forwardedRef: any,
  ) => {
    const hasLink = link.path !== ''
    const hasClickEvent = link.path === '' && onClick
    const isClickable =
      (hasLink || hasClickEvent) && sentiment === Sentiment.DEFAULT

    // define the card that can be returned as is or wrapped in a Link
    const cardContents = (
      <>
        <div
          className={`${contentStyles({
            isClickable: isClickable && !hideChevron,
            hasList,
            hasFooter: footer ? true : false,
            sentiment,
            hasBgShapes,
          })} ${contentClass}`}
        >
          {/* we wrap the children with this div so that it allows content to
          render full width, while also making space for the icon when
          it's a clickable card  */}
          <div className={`${childrenStyles(hasBgShapes)} ${childrenClass}`}>
            {children}
          </div>

          {/* Clickable cards may or may not have a chevron on the right-hand side */}
          {isClickable && !hideChevron && (
            <Icon
              className={cardLinkChevronStyles(hasBgShapes)}
              name="chevronRight"
              type={IconType.SYSTEM}
              size={IconSize.SM}
            />
          )}

          {hasBgShapes && <BackgroundShapes sentiment={sentiment} />}
        </div>

        {!!footer && (
          <div className={footerStyles} data-testid="info_cardFooter">
            {footer}
          </div>
        )}
      </>
    )

    //TODO[Jacob]: Create a LinkCard component that handles having a link
    // versus complicating the Card component with this logic, consider
    // what that also means for the modal (i.e. ModalCard?) not sure.

    // if there is a nextjs link component, use a clickable card */
    if (linkComponent) {
      return React.createElement(linkComponent, {
        href: link?.path,
        'data-testid': testId,
        rel: 'noopener noreferrer',
        className: `${cardStyles({
          isClickable,
          sentiment: sentiment,
        })} ${className}`,
        id: id,
        ref: forwardedRef,
        children: cardContents,
      })
    }

    // if there is an on click action (ex: modal), use a clickable card
    if (hasClickEvent && !hasLink) {
      return (
        <button
          // [&>*]:pointer-events-none makes heap ignore tracking of the row's children
          className={`${cardStyles({
            isClickable,
            sentiment: sentiment,
          })} ${className} [&>*]:pointer-events-none`}
          data-testid={testId}
          id={id}
          onClick={onClick}
          ref={forwardedRef}
          // setting type here so a form does not accidentally think it's a submit button
          type="button"
          // ...rest contains undefined params from radix and other 3rd parties
          {...rest}
        >
          {cardContents}
        </button>
      )
    }

    // if there is a link, use a clickable Link card
    if (hasLink && !hasClickEvent) {
      return (
        <Link
          type={link.type}
          path={link.path}
          onClick={onClick}
          className={`${cardStyles({
            isClickable,
            sentiment: sentiment,
          })} ${className}`}
          testId={testId}
          id={id}
          ref={forwardedRef}
          // ...rest contains undefined params from radix and other 3rd parties
          {...rest}
        >
          {cardContents}
        </Link>
      )
    }

    // if there is no link or on click action, use a non-clickable card
    if (!hasLink && !hasClickEvent) {
      return (
        <section
          className={`${cardStyles({
            sentiment: sentiment,
          })} ${className}`}
          data-testid={testId}
          id={id}
          ref={forwardedRef}
          // ...rest contains undefined params from radix and other 3rd parties
          {...rest}
        >
          {cardContents}
        </section>
      )
    }
  },
)
